import { useEffect, useState } from 'react';
import type { RecurrenceRule, Weekday } from '@xsc/shared';
import { Checkbox, Input, Select } from './ui/input.js';
import { FormField } from './ui/form.js';

/**
 * Composable recurrence editor used by event-new and campaign-new. Emits a
 * RecurrenceRule (or null when disabled). EventUpdateSchema deliberately
 * excludes recurrence — changing the rule on an existing event would require
 * reconciling already-expanded children, so recurrence is set once at
 * create time. Edit forms therefore omit this component.
 */

const FREQUENCIES = [
  { value: 'daily', label: 'Daily', singular: 'day' },
  { value: 'weekly', label: 'Weekly', singular: 'week' },
  { value: 'monthly', label: 'Monthly', singular: 'month' },
  { value: 'yearly', label: 'Yearly', singular: 'year' },
  { value: 'weekdays', label: 'Weekdays only', singular: 'weekday' },
] as const;

const WEEKDAYS: { value: Weekday; label: string }[] = [
  { value: 'sun', label: 'Sun' },
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
];

type EndKind = 'never' | 'count' | 'until';

export interface RecurrenceEditorProps {
  value: RecurrenceRule | null;
  onChange: (value: RecurrenceRule | null) => void;
  errors?: Record<string, string> | null;
}

export function RecurrenceEditor({ value, onChange, errors }: RecurrenceEditorProps): JSX.Element {
  const enabled = value !== null;

  // Local mirror so toggling Recurring off doesn't lose the user's prior
  // settings. When they re-enable, we restore the last shape.
  const [draft, setDraft] = useState<RecurrenceRule>(
    value ?? { frequency: 'weekly', interval: 1 },
  );
  const [endKind, setEndKind] = useState<EndKind>(() => {
    if (value?.count != null) return 'count';
    if (value?.until != null) return 'until';
    return 'never';
  });

  useEffect(() => {
    if (!enabled) return;
    onChange(draft);
  }, [draft, enabled]);

  const toggle = (on: boolean): void => {
    if (on) onChange(draft);
    else onChange(null);
  };

  const updateDraft = (patch: Partial<RecurrenceRule>): void => {
    setDraft((d) => ({ ...d, ...patch }));
  };

  const setEnd = (kind: EndKind): void => {
    setEndKind(kind);
    if (kind === 'never') updateDraft({ count: null, until: null });
    if (kind === 'count') updateDraft({ count: draft.count ?? 5, until: null });
    if (kind === 'until') updateDraft({ until: draft.until ?? '', count: null });
  };

  const toggleWeekday = (d: Weekday): void => {
    const current = draft.weekdays ?? [];
    updateDraft({
      weekdays: current.includes(d) ? current.filter((x) => x !== d) : [...current, d],
    });
  };

  const freq = FREQUENCIES.find((f) => f.value === draft.frequency)!;
  const supportsInterval = draft.frequency !== 'weekdays';

  return (
    <fieldset className="rounded border border-border p-4 space-y-3">
      <legend className="px-1 text-sm font-medium text-ink-muted">Recurrence</legend>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={enabled} onChange={(e) => toggle(e.target.checked)} />
        Recurring event
      </label>

      {enabled && (
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Frequency" error={errors?.['recurrence.frequency']}>
              {(id) => (
                <Select
                  id={id}
                  value={draft.frequency}
                  onChange={(e) => updateDraft({ frequency: e.target.value as RecurrenceRule['frequency'] })}
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </Select>
              )}
            </FormField>

            {supportsInterval && (
              <FormField
                label="Every"
                hint={`Every N ${freq.singular}${draft.interval > 1 ? 's' : ''}`}
                error={errors?.['recurrence.interval']}
              >
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min={1}
                    max={999}
                    value={draft.interval}
                    onChange={(e) =>
                      updateDraft({ interval: Math.max(1, Number.parseInt(e.target.value, 10) || 1) })
                    }
                  />
                )}
              </FormField>
            )}
          </div>

          {draft.frequency === 'weekdays' && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Days</label>
              <div className="flex gap-1">
                {WEEKDAYS.map((d) => {
                  const on = (draft.weekdays ?? []).includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggleWeekday(d.value)}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                        on
                          ? 'bg-accent text-accent-fg'
                          : 'bg-surface-alt text-ink-muted hover:text-ink border border-border'
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
              {errors?.['recurrence.weekdays'] && (
                <p className="text-xs text-danger mt-1">{errors['recurrence.weekdays']}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1.5">Ends</label>
            <div className="space-y-2">
              <RadioRow
                label="Never"
                checked={endKind === 'never'}
                onChange={() => setEnd('never')}
              />
              <RadioRow
                label="After"
                checked={endKind === 'count'}
                onChange={() => setEnd('count')}
                trailing={
                  endKind === 'count' && (
                    <>
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        className="w-20"
                        value={draft.count ?? 5}
                        onChange={(e) =>
                          updateDraft({
                            count: Math.max(1, Number.parseInt(e.target.value, 10) || 1),
                          })
                        }
                      />
                      <span className="text-sm text-ink-muted">occurrences (max 50)</span>
                    </>
                  )
                }
              />
              <RadioRow
                label="On date"
                checked={endKind === 'until'}
                onChange={() => setEnd('until')}
                trailing={
                  endKind === 'until' && (
                    <Input
                      type="datetime-local"
                      value={isoToLocalInput(draft.until ?? '')}
                      onChange={(e) =>
                        updateDraft({
                          until: e.target.value ? new Date(e.target.value).toISOString() : '',
                        })
                      }
                      className="flex-1"
                    />
                  )
                }
              />
            </div>
            {errors?.['recurrence.until'] && (
              <p className="text-xs text-danger mt-1">{errors['recurrence.until']}</p>
            )}
          </div>
        </div>
      )}
    </fieldset>
  );
}

function RadioRow({
  label,
  checked,
  onChange,
  trailing,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  trailing?: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        className="accent-accent"
      />
      <span className="w-20">{label}</span>
      {trailing}
    </label>
  );
}

function isoToLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
